import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { db } from "../../firebase";
import { useToast } from "../../components/Toast";
import AppSelect from "../../components/AppSelect";
import { useIsMobile } from "../../hooks/useResponsive";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import {
  LuClipboardList,
  LuPlus,
  LuX,
  LuLoader,
  LuTriangleAlert,
  LuCircleCheck,
  LuCircleX,
  LuClock,
  LuReply,
} from "react-icons/lu";

const TABS = [
  { key: "todas", label: "Todas" },
  { key: "pendiente", label: "Pendientes" },
  { key: "aprobada", label: "Aprobadas" },
  { key: "rechazada", label: "Rechazadas" },
];

const TYPES = [
  { value: "reglamentaria", label: "Reglamentaria" },
  { value: "extraordinaria", label: "Extraordinaria" },
  { value: "estudio", label: "Por Estudio" },
  { value: "maternidad_paternidad", label: "Maternidad/Paternidad" },
  { value: "sin_goce", label: "Sin Goce de Sueldo" },
  { value: "enfermedad", label: "Por Enfermedad" },
  { value: "otro", label: "Otro" },
];

const Solicitudes = ({ userid, userName }) => {
  const isMobile = useIsMobile();
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("todas");
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    type: "reglamentaria",
    title: "",
    description: "",
    dateFrom: "",
    dateTo: "",
  });

  useEffect(() => {
    if (selectedBuilding) {
      loadRequests();
    } else {
      setLoading(false);
    }
  }, [selectedBuilding]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const buildingId = Number(selectedBuilding.buildingid);
      // No orderBy to avoid requiring composite index; sort client-side
      const q = query(
        collection(db, "employee_requests"),
        where("buildingid", "==", buildingId),
        where("employeeid", "==", String(userid))
      );
      const snap = await getDocs(q);
      const data = [];
      snap.forEach((d) => data.push({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setRequests(data);
    } catch (error) {
      console.error("Error loading requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      showToast("Completá el título y la descripción.", "error");
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "employee_requests"), {
        employeeid: String(userid),
        employeeName: userName || "Empleado",
        buildingid: Number(selectedBuilding.buildingid),
        type: form.type,
        status: "pendiente",
        title: form.title.trim(),
        description: form.description.trim(),
        dateFrom: form.dateFrom || null,
        dateTo: form.dateTo || null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        adminResponse: null,
        respondedBy: null,
      });
      setForm({ type: "reglamentaria", title: "", description: "", dateFrom: "", dateTo: "" });
      setShowModal(false);
      loadRequests();
    } catch (error) {
      console.error("Error creating request:", error);
      showToast("Error al crear la licencia.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = activeTab === "todas"
    ? requests
    : requests.filter((r) => r.status === activeTab);

  const getStatusBadge = (status) => {
    const map = {
      pendiente: { bg: "#fef3c7", color: "#92400e", label: "Pendiente", icon: LuClock },
      aprobada: { bg: "#d1fae5", color: "#065f46", label: "Aprobada", icon: LuCircleCheck },
      rechazada: { bg: "#fee2e2", color: "#991b1b", label: "Rechazada", icon: LuCircleX },
    };
    return map[status] || map.pendiente;
  };

  const getTypeLabel = (type) => {
    const found = TYPES.find((t) => t.value === type);
    return found ? found.label : type;
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString("es-UY", { day: "2-digit", month: "short", year: "numeric" });
  };

  if (!selectedBuilding) {
    return (
      <div style={styles.emptyContainer}>
        <LuTriangleAlert size={48} color="#f59e0b" />
        <h2 style={styles.emptyTitle}>Sin edificio asignado</h2>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Licencias</h1>
          <p style={styles.pageSubtitle}>{selectedBuilding.name}</p>
        </div>
        <button style={styles.newBtn} onClick={() => setShowModal(true)}>
          <LuPlus size={14} />
          Nueva Licencia
        </button>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            style={{
              ...styles.tab,
              backgroundColor: activeTab === tab.key ? "#0080ff" : "transparent",
              color: activeTab === tab.key ? "#ffffff" : "#64748b",
              boxShadow: activeTab === tab.key ? "0 2px 4px rgba(0,128,255,0.3)" : "none",
            }}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={styles.loadingCenter}>
          <LuLoader size={24} color="#0080ff" style={{ animation: "spin 1s linear infinite" }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <LuClipboardList size={40} color="#d1d5db" />
          <p style={{ color: "#6b7280", fontSize: 14, marginTop: 12 }}>
            No hay licencias {activeTab !== "todas" ? `${activeTab}s` : ""}.
          </p>
        </div>
      ) : (
        <div style={styles.list}>
          {filtered.map((req) => {
            const badge = getStatusBadge(req.status);
            const BadgeIcon = badge.icon;
            return (
              <div key={req.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <span style={styles.typeTag}>{getTypeLabel(req.type)}</span>
                    <h3 style={styles.cardTitle}>{req.title}</h3>
                  </div>
                  <span style={{
                    ...styles.statusBadge,
                    backgroundColor: badge.bg,
                    color: badge.color,
                  }}>
                    <BadgeIcon size={10} />
                    {badge.label}
                  </span>
                </div>
                <p style={styles.cardDesc}>{req.description}</p>
                <div style={styles.cardMeta}>
                  <span style={styles.metaItem}>Creada: {formatDate(req.createdAt)}</span>
                  {req.dateFrom && (
                    <span style={styles.metaItem}>
                      Desde: {req.dateFrom} {req.dateTo ? `— Hasta: ${req.dateTo}` : ""}
                    </span>
                  )}
                </div>
                {req.status !== "pendiente" && req.adminResponse && (
                  <div style={styles.responseBox}>
                    <div style={styles.responseHeader}>
                      <LuReply size={12} color="#6b7280" />
                      <span style={styles.responseLabel}>Respuesta de administración</span>
                    </div>
                    <p style={styles.responseText}>{req.adminResponse}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Nueva Licencia</h2>
              <button style={styles.closeBtn} onClick={() => setShowModal(false)}>
                <LuX size={16} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Tipo</label>
                <AppSelect
                  value={form.type}
                  onChange={(v) => setForm({ ...form, type: v })}
                  options={TYPES}
                  ariaLabel="Tipo de licencia"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Título</label>
                <input
                  type="text"
                  style={styles.formInput}
                  placeholder="Título de la licencia"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Descripción</label>
                <textarea
                  style={styles.formTextarea}
                  placeholder="Detallá tu licencia..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4}
                />
              </div>
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.formLabel}>Fecha desde</label>
                  <input
                    type="date"
                    style={styles.formInput}
                    value={form.dateFrom}
                    onChange={(e) => setForm({ ...form, dateFrom: e.target.value })}
                  />
                </div>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.formLabel}>Fecha hasta</label>
                  <input
                    type="date"
                    style={styles.formInput}
                    value={form.dateTo}
                    onChange={(e) => setForm({ ...form, dateTo: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>
                Cancelar
              </button>
              <button
                style={{
                  ...styles.submitBtn,
                  opacity: submitting ? 0.7 : 1,
                }}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <LuLoader size={14} style={{ animation: "spin 1s linear infinite" }} />
                ) : (
                  <LuPlus size={14} />
                )}
                Enviar Licencia
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: 24,
    maxWidth: 900,
    margin: "0 auto",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    gap: 12,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 400,
    color: "#111827",
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#6b7280",
    margin: "4px 0 0",
  },
  newBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    backgroundColor: "#0080ff",
    color: "#ffffff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  tabs: {
    display: "flex",
    gap: 4,
    marginBottom: 20,
    background: "#f3f4f6",
    borderRadius: 8,
    padding: 4,
    border: "1px solid #e5e7eb",
  },
  tab: {
    flex: 1,
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "inherit",
    border: "none",
    textAlign: "center",
    transition: "all 0.2s ease",
  },
  loadingCenter: {
    display: "flex",
    justifyContent: "center",
    padding: 48,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 48,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 20,
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  cardHeaderLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  typeTag: {
    fontSize: 11,
    fontWeight: 400,
    color: "#0080ff",
    backgroundColor: "#f0f7ff",
    padding: "2px 8px",
    borderRadius: 4,
    alignSelf: "flex-start",
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  statusBadge: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 400,
    padding: "4px 12px",
    borderRadius: 20,
    flexShrink: 0,
  },
  cardDesc: {
    fontSize: 14,
    color: "#6b7280",
    margin: "0 0 12px",
    lineHeight: 1.5,
  },
  cardMeta: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
  },
  metaItem: {
    fontSize: 12,
    color: "#6b7280",
  },
  responseBox: {
    marginTop: 12,
    padding: 12,
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    borderLeft: "3px solid #0080ff",
  },
  responseHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 6,
  },
  responseLabel: {
    fontSize: 12,
    fontWeight: 400,
    color: "#6b7280",
  },
  responseText: {
    fontSize: 13,
    color: "#374151",
    margin: 0,
    lineHeight: 1.5,
  },
  // Modal
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#ffffff",
    borderRadius: 16,
    width: "100%",
    maxWidth: 520,
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    borderBottom: "1px solid #e5e7eb",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#6b7280",
    cursor: "pointer",
    padding: 4,
  },
  modalBody: {
    padding: "20px 24px",
  },
  formGroup: {
    marginBottom: 16,
  },
  formLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 400,
    color: "#374151",
    marginBottom: 6,
  },
  formInput: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    boxSizing: "border-box",
  },
  formSelect: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    backgroundColor: "#ffffff",
    boxSizing: "border-box",
  },
  formTextarea: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    resize: "vertical",
    boxSizing: "border-box",
  },
  formRow: {
    display: "flex",
    gap: 12,
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "16px 24px",
    borderTop: "1px solid #e5e7eb",
  },
  cancelBtn: {
    padding: "10px 20px",
    backgroundColor: "#ffffff",
    color: "#374151",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  submitBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    backgroundColor: "#0080ff",
    color: "#ffffff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
};

export default Solicitudes;
